import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Phone, Mail, MapPin } from "lucide-react";
import { CONTACT, BRAND } from "../../data/content";
import { Reveal } from "../Reveal";
import { MagneticButton } from "../Magnetic";

// Free form-to-email service — no backend/database needed.
// Get your free access key at https://web3forms.com and paste it below.
const WEB3FORMS_ACCESS_KEY = "e4ac5f8c-6970-46c2-8a3e-e1564c1d6771";

export default function Contact() {
  const [form, setForm] = useState({ name: "", contact: "", project_type: CONTACT.projectTypes[0], message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.contact.trim() || !form.message.trim()) {
      toast.error("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `New enquiry from Aarambh Buildworks website — ${form.name}`,
          name: form.name,
          contact: form.contact,
          project_type: form.project_type,
          message: form.message,
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Thank you — we'll be in touch shortly.");
        setForm({ name: "", contact: "", project_type: CONTACT.projectTypes[0], message: "" });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full border-b border-gold/20 bg-transparent py-4 text-lg font-light text-offwhite placeholder:text-offwhite/30 focus:border-gold focus:outline-none transition-colors duration-300";

  return (
    <section id="contact" className="relative bg-navy-900 pt-24 sm:pt-32 md:pt-40">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-12">
        <div className="grid gap-16 lg:grid-cols-2 lg:gap-24">
          {/* left: pitch + details */}
          <div>
            <Reveal>
              <p className="mb-4 text-[11px] uppercase tracking-[0.4em] text-gold">Start a project</p>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-heading text-5xl font-light leading-[0.95] text-offwhite sm:text-6xl lg:text-7xl">
                Let’s build
                <br />
                <span className="italic text-gold">something lasting.</span>
              </h2>
            </Reveal>

            <div className="mt-14 space-y-8">
              <Reveal>
                <a href={`mailto:${CONTACT.email}`} data-cursor="hover" className="flex items-center gap-4 text-offwhite/80 transition-colors hover:text-gold">
                  <Mail size={18} className="text-gold" />
                  <span className="text-lg font-light">{CONTACT.email}</span>
                </a>
              </Reveal>
              <Reveal delay={0.05}>
                <div className="flex items-center gap-4 text-offwhite/80">
                  <Phone size={18} className="text-gold" />
                  <span className="text-lg font-light">{CONTACT.phones.join("  ·  ")}</span>
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="flex items-start gap-4">
                  <MapPin size={18} className="mt-1 text-gold" />
                  <div className="flex flex-wrap gap-x-8 gap-y-2">
                    {CONTACT.offices.map((o) => (
                      <div key={o.city}>
                        <p className="text-base text-offwhite">{o.city}</p>
                        <p className="text-xs uppercase tracking-[0.15em] text-offwhite/40">{o.area}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* right: form */}
          <Reveal delay={0.1}>
            <form data-testid="enquiry-form" onSubmit={submit} className="space-y-8">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-offwhite/40">Name</label>
                <input data-testid="input-name" value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-offwhite/40">Email or phone</label>
                <input data-testid="input-contact" value={form.contact} onChange={set("contact")} placeholder="How we reach you" className={inputCls} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-offwhite/40">Project type</label>
                <select data-testid="select-project-type" value={form.project_type} onChange={set("project_type")} className={inputCls + " cursor-pointer"}>
                  {CONTACT.projectTypes.map((t) => (
                    <option key={t} value={t} className="bg-navy-800 text-offwhite">{t}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-offwhite/40">Message</label>
                <textarea data-testid="input-message" value={form.message} onChange={set("message")} rows={3} placeholder="Tell us about the space" className={inputCls + " resize-none"} />
              </div>
              <MagneticButton as="button" type="submit" data-testid="submit-enquiry" disabled={loading}>
                {loading ? "Sending…" : "Send enquiry"}
                <ArrowUpRight size={16} />
              </MagneticButton>
            </form>
          </Reveal>
        </div>

        {/* footer */}
        <footer className="mt-28 flex flex-col items-start justify-between gap-6 border-t border-gold/10 py-10 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.png" alt="Aarambh Buildworks" className="h-10 w-auto" />
            <span className="font-heading text-2xl text-offwhite">Aarambh</span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold">Buildworks</span>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-offwhite/40">{BRAND.tagline}</p>
          <p className="text-xs text-offwhite/30">© {new Date().getFullYear()} Aarambh Buildworks</p>
        </footer>
      </div>
    </section>
  );
}
